import { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BoltIcon,
  ListBulletIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState } from '@/components/common';
import { webhooksApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Webhook {
  _id: string;
  url: string;
  events: string[];
  description?: string;
  active: boolean;
  organization_id?: string;
  created_at: string;
  updated_at: string;
}

interface WebhookDelivery {
  _id: string;
  webhook_id: string;
  event: string;
  status: 'success' | 'failed' | 'pending';
  attempts: number;
  response_status?: number;
  response_body?: string;
  error?: string;
  created_at: string;
}

// ─── Event groups ──────────────────────────────────────────────────────────────

const EVENT_GROUPS: { label: string; prefix: string; events: string[] }[] = [
  {
    label: 'Usuario',
    prefix: 'user.',
    events: [
      'user.login',
      'user.logout',
      'user.created',
      'user.updated',
      'user.deleted',
      'user.password_changed',
    ],
  },
  {
    label: 'Organización',
    prefix: 'org.',
    events: [
      'org.created',
      'org.updated',
      'org.deleted',
      'org.suspended',
    ],
  },
  {
    label: 'Rol',
    prefix: 'role.',
    events: [
      'role.created',
      'role.updated',
      'role.deleted',
      'role.assigned',
      'role.revoked',
    ],
  },
  {
    label: 'Base de datos',
    prefix: 'db.',
    events: [
      'db.created',
      'db.deleted',
      'db.backup',
      'db.restore',
    ],
  },
  {
    label: 'Contenido',
    prefix: 'content.',
    events: [
      'content.created',
      'content.updated',
      'content.deleted',
    ],
  },
  {
    label: 'Configuración',
    prefix: 'settings.',
    events: [
      'settings.updated',
    ],
  },
  {
    label: 'Ecommerce',
    prefix: 'ecommerce.',
    events: [
      'ecommerce.order.created',
      'ecommerce.order.updated',
      'ecommerce.order.cancelled',
      'ecommerce.product.created',
      'ecommerce.product.updated',
      'ecommerce.product.deleted',
    ],
  },
  {
    label: 'Catálogo',
    prefix: 'catalog.',
    events: [
      'catalog.product.created',
      'catalog.product.updated',
      'catalog.product.deleted',
    ],
  },
  {
    label: 'Clientes',
    prefix: 'customers.',
    events: [
      'customers.created',
      'customers.updated',
      'customers.deleted',
    ],
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(date: string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return formatDate(date);
}

function apiError(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function EventsCheckboxGroup({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (events: string[]) => void;
}) {
  const toggle = (event: string) => {
    onChange(
      selected.includes(event)
        ? selected.filter((e) => e !== event)
        : [...selected, event]
    );
  };

  const toggleGroup = (events: string[]) => {
    const allSelected = events.every((e) => selected.includes(e));
    if (allSelected) {
      onChange(selected.filter((e) => !events.includes(e)));
    } else {
      const merged = Array.from(new Set([...selected, ...events]));
      onChange(merged);
    }
  };

  return (
    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
      {EVENT_GROUPS.map((group) => {
        const allChecked = group.events.every((e) => selected.includes(e));
        const someChecked = group.events.some((e) => selected.includes(e));
        return (
          <div key={group.prefix} className="border border-gray-200 rounded-lg p-3">
            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked && !allChecked;
                }}
                onChange={() => toggleGroup(group.events)}
                className="w-4 h-4 rounded border-gray-300 text-vida-600 focus:ring-vida-500"
              />
              <span className="text-sm font-medium text-gray-700">{group.label}</span>
            </label>
            <div className="flex flex-wrap gap-1.5 pl-6">
              {group.events.map((event) => (
                <label
                  key={event}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs cursor-pointer transition-colors border ${
                    selected.includes(event)
                      ? 'bg-vida-100 text-vida-700 border-vida-300'
                      : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={selected.includes(event)}
                    onChange={() => toggle(event)}
                  />
                  {event}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DeliveryStatusBadge({ status }: { status: WebhookDelivery['status'] }) {
  if (status === 'success') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircleIcon className="w-3.5 h-3.5" />
        Exitoso
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircleIcon className="w-3.5 h-3.5" />
        Fallido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
      <ClockIcon className="w-3.5 h-3.5" />
      Pendiente
    </span>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

const emptyForm = { url: '', events: [] as string[], description: '' };

export default function WebhooksPage() {
  // List state
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Selected webhook
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeliveriesModal, setShowDeliveriesModal] = useState(false);

  // Form
  const [formData, setFormData] = useState(emptyForm);
  const [editFormData, setEditFormData] = useState(emptyForm);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);

  // Deliveries
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [deliveriesTotal, setDeliveriesTotal] = useState(0);
  const deliveriesLimit = 15;

  // ── Load webhooks ────────────────────────────────────────────────────────────

  useEffect(() => {
    loadWebhooks();
  }, [page]);

  const loadWebhooks = async () => {
    try {
      const response = await webhooksApi.list(page, limit);
      setWebhooks(response.data.webhooks || []);
      setTotal(response.data.total || 0);
    } catch {
      toast.error('Error al cargar webhooks');
    } finally {
      setLoading(false);
    }
  };

  // ── Create ───────────────────────────────────────────────────────────────────

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.url) {
      toast.error('La URL es requerida');
      return;
    }
    if (formData.events.length === 0) {
      toast.error('Selecciona al menos un evento');
      return;
    }

    setFormSubmitting(true);
    try {
      await webhooksApi.create({
        url: formData.url,
        events: formData.events,
        description: formData.description || undefined,
      });
      toast.success('Webhook creado');
      setShowCreateModal(false);
      setFormData(emptyForm);
      loadWebhooks();
    } catch (error) {
      toast.error(apiError(error, 'Error al crear webhook'));
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Update ───────────────────────────────────────────────────────────────────

  const openEditModal = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setEditFormData({
      url: webhook.url,
      events: webhook.events,
      description: webhook.description || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWebhook) return;
    if (!editFormData.url) {
      toast.error('La URL es requerida');
      return;
    }
    if (editFormData.events.length === 0) {
      toast.error('Selecciona al menos un evento');
      return;
    }

    setFormSubmitting(true);
    try {
      await webhooksApi.update(selectedWebhook._id, {
        url: editFormData.url,
        events: editFormData.events,
        description: editFormData.description || undefined,
      });
      toast.success('Webhook actualizado');
      setShowEditModal(false);
      setSelectedWebhook(null);
      loadWebhooks();
    } catch (error) {
      toast.error(apiError(error, 'Error al actualizar webhook'));
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Toggle active ────────────────────────────────────────────────────────────

  const handleToggleActive = async (webhook: Webhook) => {
    try {
      await webhooksApi.update(webhook._id, { active: !webhook.active });
      toast.success(webhook.active ? 'Webhook desactivado' : 'Webhook activado');
      loadWebhooks();
    } catch (error) {
      toast.error(apiError(error, 'Error al cambiar estado'));
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const isSuperAdmin = useAuthStore((state) => state.hasPermission('*'));

  const openDeleteConfirm = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!selectedWebhook) return;
    setDeleteLoading(true);
    try {
      await webhooksApi.delete(selectedWebhook._id);
      toast.success('Webhook eliminado');
      setShowDeleteConfirm(false);
      setSelectedWebhook(null);
      loadWebhooks();
    } catch (error) {
      toast.error(apiError(error, 'Error al eliminar webhook'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Test ping ────────────────────────────────────────────────────────────────

  const handleTest = async (webhook: Webhook) => {
    setTestingId(webhook._id);
    try {
      await webhooksApi.test(webhook._id);
      toast.success('Ping enviado correctamente');
    } catch (error) {
      toast.error(apiError(error, 'Error al enviar ping'));
    } finally {
      setTestingId(null);
    }
  };

  // ── Deliveries ───────────────────────────────────────────────────────────────

  const openDeliveries = async (webhook: Webhook, pg = 1) => {
    setSelectedWebhook(webhook);
    setDeliveriesPage(pg);
    setShowDeliveriesModal(true);
    setDeliveriesLoading(true);
    try {
      const response = await webhooksApi.getDeliveries(webhook._id, pg, deliveriesLimit);
      setDeliveries(response.data.deliveries || []);
      setDeliveriesTotal(response.data.total || 0);
    } catch {
      toast.error('Error al cargar deliveries');
    } finally {
      setDeliveriesLoading(false);
    }
  };

  const handleDeliveriesPageChange = (newPage: number) => {
    if (!selectedWebhook) return;
    openDeliveries(selectedWebhook, newPage);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Header
        title="Webhooks"
        subtitle="Notificaciones HTTP salientes para eventos del sistema"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Crear Webhook
          </button>
        }
      />

      <div className="p-6 space-y-6">
        {webhooks.length === 0 ? (
          <EmptyState
            title="No hay webhooks configurados"
            description="Crea un webhook para recibir notificaciones HTTP cuando ocurran eventos en el sistema"
          />
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      URL
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Eventos
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Descripcion
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Creado
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {webhooks.map((webhook) => (
                    <tr key={webhook._id} className="hover:bg-gray-50 transition-colors">
                      {/* URL */}
                      <td className="px-4 py-3">
                        <span
                          className="font-mono text-xs text-gray-800 truncate block max-w-[220px]"
                          title={webhook.url}
                        >
                          {webhook.url}
                        </span>
                      </td>

                      {/* Events badge count */}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {webhook.events.length} evento{webhook.events.length !== 1 ? 's' : ''}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleActive(webhook)}
                          title={webhook.active ? 'Desactivar' : 'Activar'}
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                            webhook.active
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {webhook.active ? (
                            <>
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Activo
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="w-3.5 h-3.5" />
                              Inactivo
                            </>
                          )}
                        </button>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-500 text-xs truncate block max-w-[180px]">
                          {webhook.description || <span className="italic text-gray-300">Sin descripcion</span>}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-gray-400 text-xs">{formatRelative(webhook.created_at)}</span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit */}
                          <button
                            onClick={() => openEditModal(webhook)}
                            title="Editar"
                            className="p-1.5 rounded text-gray-400 hover:text-vida-600 hover:bg-vida-50 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>

                          {/* Test ping */}
                          <button
                            onClick={() => handleTest(webhook)}
                            title="Enviar ping de prueba"
                            disabled={testingId === webhook._id}
                            className="p-1.5 rounded text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50"
                          >
                            {testingId === webhook._id ? (
                              <span className="block w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <BoltIcon className="w-4 h-4" />
                            )}
                          </button>

                          {/* View deliveries */}
                          <button
                            onClick={() => openDeliveries(webhook)}
                            title="Ver entregas"
                            className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <ListBulletIcon className="w-4 h-4" />
                          </button>

                          {/* Delete: solo super_admin (VIDA-347) */}
                          {isSuperAdmin && (
                            <button
                              onClick={() => openDeleteConfirm(webhook)}
                              title="Eliminar"
                              className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(page - 1) * limit + 1}-{Math.min(page * limit, total)} de {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-secondary text-sm"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * limit >= total}
                    className="btn-secondary text-sm"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Create modal ───────────────────────────────────────────────────────── */}
      <Modal
        open={showCreateModal}
        onClose={() => { setShowCreateModal(false); setFormData(emptyForm); }}
        title="Crear Webhook"
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="label">URL del endpoint *</label>
            <input
              type="url"
              className="input"
              placeholder="https://example.com/webhook"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label mb-2">Eventos *</label>
            <EventsCheckboxGroup
              selected={formData.events}
              onChange={(events) => setFormData({ ...formData, events })}
            />
            {formData.events.length > 0 && (
              <p className="text-xs text-gray-500 mt-1.5">
                {formData.events.length} evento{formData.events.length !== 1 ? 's' : ''} seleccionado{formData.events.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <div>
            <label className="label">Descripcion</label>
            <textarea
              className="input resize-none"
              rows={2}
              placeholder="Descripcion opcional del webhook"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setShowCreateModal(false); setFormData(emptyForm); }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={formSubmitting}>
              {formSubmitting ? 'Creando...' : 'Crear Webhook'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Edit modal ─────────────────────────────────────────────────────────── */}
      <Modal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedWebhook(null); }}
        title="Editar Webhook"
        size="lg"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="label">URL del endpoint *</label>
            <input
              type="url"
              className="input"
              placeholder="https://example.com/webhook"
              value={editFormData.url}
              onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label mb-2">Eventos *</label>
            <EventsCheckboxGroup
              selected={editFormData.events}
              onChange={(events) => setEditFormData({ ...editFormData, events })}
            />
            {editFormData.events.length > 0 && (
              <p className="text-xs text-gray-500 mt-1.5">
                {editFormData.events.length} evento{editFormData.events.length !== 1 ? 's' : ''} seleccionado{editFormData.events.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <div>
            <label className="label">Descripcion</label>
            <textarea
              className="input resize-none"
              rows={2}
              placeholder="Descripcion opcional del webhook"
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setShowEditModal(false); setSelectedWebhook(null); }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={formSubmitting}>
              {formSubmitting ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Delete confirm ─────────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setSelectedWebhook(null); }}
        onConfirm={handleDelete}
        title="Eliminar Webhook"
        message={`Se eliminara el webhook "${selectedWebhook?.url}". Esta accion no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />

      {/* ── Deliveries modal ───────────────────────────────────────────────────── */}
      <Modal
        open={showDeliveriesModal}
        onClose={() => { setShowDeliveriesModal(false); setDeliveries([]); setSelectedWebhook(null); }}
        title="Historial de entregas"
        size="xl"
      >
        <div className="space-y-3">
          {selectedWebhook && (
            <p className="text-xs font-mono text-gray-500 truncate">{selectedWebhook.url}</p>
          )}

          {deliveriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <span className="block w-8 h-8 border-4 border-vida-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ListBulletIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No hay entregas registradas para este webhook</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Evento
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Intentos
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        HTTP
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deliveries.map((delivery) => (
                      <tr key={delivery._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2.5">
                          <span className="font-mono text-xs text-gray-700">{delivery.event}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <DeliveryStatusBadge status={delivery.status} />
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs text-gray-600">{delivery.attempts}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          {delivery.response_status ? (
                            <span
                              className={`text-xs font-mono font-semibold ${
                                delivery.response_status >= 200 && delivery.response_status < 300
                                  ? 'text-green-600'
                                  : 'text-red-500'
                              }`}
                            >
                              {delivery.response_status}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs text-gray-400">{formatRelative(delivery.created_at)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Deliveries pagination */}
              {deliveriesTotal > deliveriesLimit && (
                <div className="flex items-center justify-between pt-1">
                  <p className="text-xs text-gray-500">
                    {(deliveriesPage - 1) * deliveriesLimit + 1}-{Math.min(deliveriesPage * deliveriesLimit, deliveriesTotal)} de {deliveriesTotal}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDeliveriesPageChange(deliveriesPage - 1)}
                      disabled={deliveriesPage === 1}
                      className="btn-secondary text-xs py-1 px-3"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => handleDeliveriesPageChange(deliveriesPage + 1)}
                      disabled={deliveriesPage * deliveriesLimit >= deliveriesTotal}
                      className="btn-secondary text-xs py-1 px-3"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
